import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, ArrowClockwise, CircleNotch, ClockCounterClockwise, Warning, GitMerge } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGNAL_LABEL = {
  doi_exact_match: "DOI exact match",
  pmid_exact_match: "PMID exact match",
  trial_registration_match: "Trial registration ID match",
  title_similarity: "Title similarity",
  title_and_year_partial: "Title & year partial match",
  author_overlap: "Author-name overlap",
};

export default function MergeReviewPage({ embedded }) {
  const { productId, product } = useProduct();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [audit, setAudit] = useState([]);
  const [counts, setCounts] = useState(null);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null); // {review, mode: "approve"|"reject", target_publication_id, reason}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, a, c] = await Promise.all([
        axios.get(`${API}/identity/review-queue`, { params: { product_id: productId, status: tab } }),
        axios.get(`${API}/identity/audit`, { params: { product_id: productId } }),
        axios.get(`${API}/identity/unique-counts`, { params: { product_id: productId } }),
      ]);
      setItems(q.data); setAudit(a.data); setCounts(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load merge queue");
    } finally { setLoading(false); }
  }, [productId, tab]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!decision) return;
    const reason = (decision.reason || "").trim();
    if (reason.length < 5) { toast.error("Reason must be at least 5 characters"); return; }
    try {
      if (decision.mode === "approve") {
        if (!decision.target_publication_id) return toast.error("Pick a target publication first");
        await axios.post(`${API}/identity/review-queue/${decision.review.id}/approve`, {
          target_publication_id: decision.target_publication_id, reason,
        });
        toast.success("Merged");
      } else {
        await axios.post(`${API}/identity/review-queue/${decision.review.id}/reject`, { reason });
        toast.success("Kept separate");
      }
      setDecision(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resolve review");
    }
  };

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Review", to: "/console/evidence-review?tab=duplicates" }} pageId="merge-review" overline={`MAI Knowledge Hub™ · Merge Review · ${product?.name || ""}`}
      title="Evidence Identity — Merge Review Queue">

      {/* Counts strip */}
      {counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="merge-counts">
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Studies</p>
            <p className="font-mono-data text-2xl font-bold text-slate-800">{counts.unique_studies}</p>
          </div>
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Unique publications</p>
            <p className="font-mono-data text-2xl font-bold text-emerald-700">{counts.unique_publications}</p>
          </div>
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Unique claims</p>
            <p className="font-mono-data text-2xl font-bold text-slate-800">{counts.unique_claims}</p>
          </div>
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[10px] uppercase text-slate-500 tracking-wide font-semibold">Evidence records (raw)</p>
            <p className="font-mono-data text-2xl font-bold text-slate-500">{counts.total_assets}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        {["pending", "approved_merge", "rejected_kept_separate"].map((t) => (
          <button key={t} data-testid={`merge-tab-${t}`} onClick={() => setTab(t)}
                  className={`text-xs px-3 py-1.5 border ${tab === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}>
            {t === "pending" ? "Pending" : t === "approved_merge" ? "Merged" : "Kept separate"}
          </button>
        ))}
        <Button data-testid="merge-refresh-btn" onClick={load} disabled={loading}
                variant="outline" className="text-xs rounded-none ml-auto">
          {loading ? <CircleNotch size={12} className="animate-spin" /> : <ArrowClockwise size={12} />}
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      {/* Queue */}
      {items.length === 0 && !loading && (
        <div data-testid="merge-empty-state" className="border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm text-slate-600">No {tab.replace("_", " ")} reviews for this product.</p>
          <p className="text-xs text-slate-400 mt-1">
            Duplicate publications land here automatically whenever a new record shares a DOI, PMID, trial-registration ID or high-similarity title with an existing one.
          </p>
        </div>
      )}

      {items.map((it) => (
        <div key={it.id} data-testid={`merge-review-${it.id}`} className="border border-slate-200 bg-white mb-4">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-bold text-slate-800">Candidate: {it.candidate.title}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">
                {(it.candidate.authors || []).slice(0, 3).join(", ")}
                {it.candidate.year ? ` · ${it.candidate.year}` : ""}
                {it.candidate.doi ? ` · DOI ${it.candidate.doi}` : ""}
                {it.candidate.pmid ? ` · PMID ${it.candidate.pmid}` : ""}
              </p>
            </div>
            <span className="text-[10px] px-2 py-0.5 border font-bold uppercase tracking-wider"
                  style={{ color: it.top_confidence >= 0.9 ? "#B91C1C" : "#B45309",
                            borderColor: it.top_confidence >= 0.9 ? "#B91C1C" : "#B45309" }}>
              {(it.top_confidence * 100).toFixed(0)}% confidence
            </span>
          </div>
          <div className="p-4">
            <p className="text-[10px] uppercase font-semibold text-slate-500 mb-2">Existing publications that look like duplicates</p>
            {it.matches.map((m) => (
              <div key={m.existing_publication.id} className="border border-slate-200 mb-2 last:mb-0">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-800">{m.existing_publication.title}</p>
                    <p className="text-[11px] text-slate-500">
                      {(m.existing_publication.authors || []).slice(0, 3).join(", ")}
                      {m.existing_publication.year ? ` · ${m.existing_publication.year}` : ""}
                      {m.existing_publication.doi ? ` · DOI ${m.existing_publication.doi}` : ""}
                    </p>
                  </div>
                  {tab === "pending" && isAdmin && (
                    <Button data-testid={`merge-approve-${it.id}-${m.existing_publication.id}-btn`}
                            onClick={() => setDecision({ review: it, mode: "approve", target_publication_id: m.existing_publication.id, reason: "" })}
                            className="text-[10px] bg-emerald-700 hover:bg-emerald-800 px-2 py-1 h-auto rounded-none">
                      <GitMerge size={11} /><span className="ml-1">Merge into this</span>
                    </Button>
                  )}
                </div>
                <div className="px-3 py-2 flex flex-wrap gap-1.5">
                  {m.signals.map((sig, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 border border-slate-200 text-slate-700">
                      {SIGNAL_LABEL[sig.kind] || sig.kind}
                      {sig.value !== undefined && `: ${Array.isArray(sig.value) ? sig.value.join(", ") : sig.value}`}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {tab === "pending" && isAdmin && (
              <div className="mt-3 flex justify-end">
                <Button data-testid={`merge-reject-${it.id}-btn`}
                        onClick={() => setDecision({ review: it, mode: "reject", reason: "" })}
                        variant="outline" className="text-xs rounded-none">
                  <XCircle size={12} /><span className="ml-1.5">Keep separate</span>
                </Button>
              </div>
            )}
            {it.resolution_reason && (
              <p className="mt-3 text-[11px] text-slate-500 italic">
                Resolved by {it.resolved_by} — &ldquo;{it.resolution_reason}&rdquo;
              </p>
            )}
          </div>
        </div>
      ))}

      {/* Audit trail (compact) */}
      {audit.length > 0 && (
        <div className="border border-slate-200 bg-white mt-8" data-testid="merge-audit-trail">
          <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <ClockCounterClockwise size={12} />
            <p className="font-heading font-bold text-xs uppercase tracking-wide text-slate-700">Audit trail</p>
          </div>
          <div className="max-h-60 overflow-auto divide-y divide-slate-100">
            {audit.slice(0, 20).map((a) => (
              <div key={a.id} className="px-4 py-2 text-[11px]">
                <p className="text-slate-700"><b className={a.action === "merge" ? "text-emerald-700" : "text-slate-600"}>{a.action}</b> · {a.approver_email} · {new Date(a.created_at).toLocaleString()}</p>
                <p className="text-slate-500 italic mt-0.5">&ldquo;{a.reason}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Decision dialog */}
      {decision && (
        <div data-testid="merge-decision-dialog" className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-4"
             onClick={() => setDecision(null)}>
          <div className="bg-white border border-slate-300 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-slate-200 px-4 py-3 bg-slate-50">
              <p className="text-sm font-bold text-slate-800">
                {decision.mode === "approve" ? "Merge publication" : "Keep publications separate"}
              </p>
              <p className="text-[11px] text-slate-500 mt-0.5">Reason will be preserved in the audit trail forever.</p>
            </div>
            <div className="px-4 py-4">
              <Textarea data-testid="merge-decision-reason"
                        value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })}
                        placeholder="Explain why these records should be merged or kept separate…"
                        rows={4} className="text-xs" />
              <div className="flex justify-end gap-2 mt-4">
                <Button data-testid="merge-decision-cancel-btn" onClick={() => setDecision(null)} variant="ghost" className="text-xs rounded-none">Cancel</Button>
                <Button data-testid="merge-decision-submit-btn" onClick={submit}
                        className={`text-xs rounded-none ${decision.mode === "approve" ? "bg-emerald-700 hover:bg-emerald-800" : "bg-slate-900 hover:bg-slate-800"}`}>
                  {decision.mode === "approve" ? "Confirm merge" : "Confirm keep separate"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
